export const RENT_CASE_TAXONOMY_CONFIG = {
  id: 'rent_tenancy',
  label: 'Rent / Tenancy',
  shortLabel: 'Rent',
  description: 'Eviction, ejectment, rent recovery, rent deposit, tenant protection, rent appeals, rent revisions, and related landlord-tenant proceedings.',
  active: true,
  types: [
    {
      id: 'Eviction / Ejectment Petition',
      label: 'Eviction / Ejectment Petition',
      shortLabel: 'Eviction',
      description: 'Proceedings by landlord/owner seeking eviction or ejectment of tenant from rented premises.',
      legalBasis: [
        'Default in payment of rent',
        'Personal bona fide need',
        'Expiry/termination of tenancy where applicable',
        'Violation of tenancy terms',
        'Subletting',
        'Change of use / misuse',
        'Structural alteration / damage',
        'Nuisance',
        'Denial of landlord title',
        'Reconstruction / renovation, where applicable'
      ],
      provinceAwareLegalBasis: {
        'Khyber Pakhtunkhwa': 'West Pakistan Urban Rent Restriction Ordinance, 1959, as applicable/adapted.',
        'Punjab': 'Punjab Rented Premises Act, 2009.',
        'Sindh': 'Sindh Rented Premises Ordinance, 1979.',
        'Islamabad Capital Territory': 'Applicable Islamabad rent law.',
        'Balochistan': 'Applicable local rent law.',
        'Other / Custom': 'Applicable local rent law.'
      },
      courtOptions: [
        { id: 'Competent Rent Forum / Rent Controller / Rent Tribunal', label: 'Competent Rent Forum / Rent Controller / Rent Tribunal', description: 'Primary forum for eviction/ejectment petitions, depending on province or territory.' },
        { id: 'Rent Tribunal', label: 'Rent Tribunal', description: 'Use where the applicable law provides Rent Tribunal, such as Punjab.' },
        { id: 'Rent Controller', label: 'Rent Controller', description: 'Use where the applicable law provides Rent Controller, such as Sindh/KP or other local law.' },
        { id: 'Civil Court / Special Rent Forum, where applicable', label: 'Civil Court / Special Rent Forum, where applicable', description: 'Use only where local law or practice assigns the matter differently.' },
        { id: 'Other / Custom Forum', label: 'Other / Custom Forum', description: 'Use if the correct forum differs.' }
      ],
      courtHelper: 'Eviction forum depends on province/territory, premises location, applicable rent statute, and local court setup.',
      sideOptions: ['Landlord / Petitioner', 'Tenant / Respondent'],
      documentTabs: [
        'All',
        'Eviction Petition',
        'Tenancy Documents',
        'Rent Record',
        'Applications',
        'Evidence',
        'Orders',
        'Miscellaneous'
      ],
      defaultStages: [
        'Case Preparation / Tenancy Record Review',
        'Notice / Demand Review, if applicable',
        'Eviction Petition Drafting',
        'Filing',
        'Scrutiny / Objections, if any',
        'Notice to Tenant',
        'Tenant Appearance',
        'Leave to Contest / Written Reply, where applicable',
        'Interim Rent / Deposit Order, if applicable',
        'Evidence / Affidavits / Record',
        'Arguments',
        'Eviction Order / Dismissal',
        'Appeal / Revision / Writ, if any',
        'Execution / Possession Proceedings',
        'Closed'
      ],
      aiContextHints: 'AI should understand landlord, tenant, premises, tenancy agreement, rent amount, arrears, default period, personal need, notice, grounds of eviction, rent record, interim rent order, and possession relief.',
      subtypes: [
        'Personal Bona Fide Need',
        'Default in Payment of Rent',
        'Expiry of Tenancy',
        'Violation of Tenancy Terms',
        'Subletting',
        'Change of Use',
        'Structural Alteration / Damage',
        'Nuisance / Misuse',
        'Reconstruction / Renovation',
        'Denial of Landlord Title',
        'Other Eviction Ground'
      ]
    },
    {
      id: 'Rent Recovery',
      label: 'Rent Recovery',
      shortLabel: 'Recovery',
      description: 'Proceedings for recovery of arrears of rent, mesne profits, use and occupation charges, utility charges, damages to premises, or related rental dues.',
      legalBasis: [
        'Applicable rent law where connected with tenancy proceedings.',
        'Where the claim is not maintainable before rent forum or is independent, civil recovery may be relevant depending on local law.'
      ],
      courtOptions: [
        { id: 'Competent Rent Forum / Rent Controller / Rent Tribunal', label: 'Competent Rent Forum / Rent Controller / Rent Tribunal', description: 'Use where the applicable rent law allows recovery or related relief within rent proceedings.' },
        { id: 'Civil Court', label: 'Civil Court', description: 'Use where recovery is filed as an ordinary civil claim or where rent forum lacks jurisdiction for the claim.' },
        { id: 'Same Forum Where Eviction Case Is Pending', label: 'Same Forum Where Eviction Case Is Pending', description: 'Use where rent recovery is connected with an existing eviction or rent case.' },
        { id: 'Other / Custom Forum', label: 'Other / Custom Forum', description: 'Use if forum differs.' }
      ],
      courtHelper: 'Recovery forum depends on whether the claim is part of rent proceedings, separate civil recovery, or governed by local rent law.',
      sideOptions: ['Landlord / Claimant', 'Tenant / Respondent'],
      documentTabs: [
        'All',
        'Recovery Claim',
        'Tenancy Documents',
        'Rent / Payment Record',
        'Applications',
        'Evidence',
        'Orders',
        'Miscellaneous'
      ],
      defaultStages: [
        'Rent Account / Arrears Review',
        'Claim / Application Drafting',
        'Filing',
        'Scrutiny / Objections, if any',
        'Notice to Respondent',
        'Reply / Objections',
        'Reconciliation / Settlement, if any',
        'Evidence / Payment Record',
        'Arguments',
        'Order / Decree',
        'Recovery / Execution, if any',
        'Closed'
      ],
      aiContextHints: 'AI should understand rent amount, default period, arrears calculation, payment record, security deposit, utility bills, mesne profits/use and occupation, and recovery prayer.',
      subtypes: [
        'Arrears of Rent',
        'Mesne Profits / Use and Occupation Charges',
        'Utility Charges',
        'Damage to Premises',
        'Security Deposit Dispute',
        'Rent Adjustment / Accounting',
        'Other Rent Recovery'
      ]
    },
    {
      id: 'Rent Deposit / Consignment',
      label: 'Rent Deposit / Consignment',
      shortLabel: 'Rent Deposit',
      description: 'Proceedings where tenant seeks permission to deposit rent before court/forum due to refusal by landlord, dispute of ownership, pending eviction, uncertainty about payee, or other rent payment dispute.',
      legalBasis: [
        'Use applicable local rent law.'
      ],
      courtOptions: [
        { id: 'Competent Rent Forum / Rent Controller / Rent Tribunal', label: 'Competent Rent Forum / Rent Controller / Rent Tribunal', description: 'Forum for deposit/consignment of rent where local rent law permits.' },
        { id: 'Same Forum Where Rent Case Is Pending', label: 'Same Forum Where Rent Case Is Pending', description: 'Use where deposit is connected with an existing eviction/rent case.' },
        { id: 'Civil Court / Treasury Deposit Route, where applicable', label: 'Civil Court / Treasury Deposit Route, where applicable', description: 'Use only where local practice requires another route.' },
        { id: 'Other / Custom Forum', label: 'Other / Custom Forum', description: 'Use if forum differs.' }
      ],
      courtHelper: 'Rent deposit procedure depends on local rent law, pending proceedings, and whether landlord refused rent.',
      sideOptions: ['Tenant / Applicant', 'Landlord / Respondent'],
      documentTabs: [
        'All',
        'Rent Deposit Application',
        'Tenancy Documents',
        'Payment Record',
        'Receipts / Challans',
        'Orders',
        'Miscellaneous'
      ],
      defaultStages: [
        'Rent Refusal / Dispute Review',
        'Deposit Application Drafting',
        'Filing',
        'Scrutiny / Objections, if any',
        'Notice to Landlord, if required',
        'Deposit Order / Permission',
        'Rent Deposit / Challan',
        'Monthly Deposit Continuation',
        'Objections / Adjustment, if any',
        'Final Order / Disposal',
        'Closed'
      ],
      aiContextHints: 'AI should understand tenant, landlord, rent amount, months due, refusal to receive rent, ownership dispute, pending eviction, deposit record, and protection from default allegation.',
      subtypes: [
        'Deposit of Monthly Rent',
        'Deposit Due to Refusal by Landlord',
        'Deposit Due to Dispute of Ownership',
        'Deposit During Pending Eviction',
        'Deposit of Arrears',
        'Other Rent Deposit Matter'
      ]
    },
    {
      id: 'Tenant Protection / Injunction',
      label: 'Tenant Protection / Injunction',
      shortLabel: 'Tenant Protection',
      description: 'Proceedings by tenant seeking protection against illegal dispossession, harassment, disconnection of utilities, locking of premises, interference with possession, or forcible eviction.',
      legalBasis: [
        'Use applicable rent law and, where appropriate, civil/injunctive or constitutional remedy context.'
      ],
      courtOptions: [
        { id: 'Competent Rent Forum / Rent Controller / Rent Tribunal', label: 'Competent Rent Forum / Rent Controller / Rent Tribunal', description: 'Use where tenant protection or restoration is provided under local rent law.' },
        { id: 'Civil Court', label: 'Civil Court', description: 'Use where injunctive protection is pursued as a civil remedy.' },
        { id: 'High Court / Constitutional Jurisdiction, where applicable', label: 'High Court / Constitutional Jurisdiction, where applicable', description: 'Use only where public authority involvement, police inaction, or constitutional/public law issue exists.' },
        { id: 'Other / Custom Forum', label: 'Other / Custom Forum', description: 'Use if forum differs.' }
      ],
      courtHelper: 'Tenant protection forum depends on whether the dispute is purely landlord-tenant, civil injunctive, or involves public authority/police action.',
      sideOptions: ['Tenant / Applicant', 'Landlord / Respondent'],
      documentTabs: [
        'All',
        'Protection Application / Suit',
        'Tenancy Documents',
        'Incident Record',
        'Applications',
        'Orders',
        'Miscellaneous'
      ],
      defaultStages: [
        'Urgent Facts / Incident Review',
        'Protection Application / Suit Drafting',
        'Filing',
        'Urgent Hearing / Interim Relief',
        'Notice to Landlord / Respondent',
        'Reply / Objections',
        'Evidence / Incident Record',
        'Arguments',
        'Protection / Injunction Order',
        'Compliance / Restoration',
        'Further Remedy, if any',
        'Closed'
      ],
      aiContextHints: 'AI should understand current possession, tenancy status, illegal dispossession threat, utility disconnection, harassment, police applications, urgency, interim relief, and status quo/protection prayer.',
      subtypes: [
        'Protection Against Illegal Dispossession',
        'Protection Against Harassment',
        'Restoration of Utilities',
        'Restraint Against Locking Premises',
        'Restraint Against Forcible Eviction',
        'Access to Premises',
        'Other Tenant Protection Matter'
      ]
    },
    {
      id: 'Rent Appeal',
      label: 'Rent Appeal',
      shortLabel: 'Appeal',
      description: 'Appeal against rent controller, rent tribunal, civil judge, or other rent forum order/judgment where maintainable.',
      legalBasis: [
        'Use province-aware appeal hints.'
      ],
      courtOptions: [
        { id: 'Appellate Rent Forum / District Judge', label: 'Appellate Rent Forum / District Judge', description: 'Common appellate forum under several rent laws, depending on province and statute.' },
        { id: 'High Court', label: 'High Court', description: 'Use where the matter reaches High Court by revision, writ, constitutional jurisdiction, or special appellate route.' },
        { id: 'Special Appellate Forum', label: 'Special Appellate Forum', description: 'Use where the applicable rent statute provides a specific appellate forum.' },
        { id: 'Other / Custom Forum', label: 'Other / Custom Forum', description: 'Use if forum differs.' }
      ],
      courtHelper: 'Rent appeal forum and limitation depend on the applicable provincial/territorial rent law and the impugned order.',
      sideOptions: ['Appellant', 'Respondent'],
      documentTabs: [
        'All',
        'Memo of Appeal',
        'Applications',
        'Impugned Order',
        'Record',
        'Orders',
        'Miscellaneous'
      ],
      defaultStages: [
        'Review of Impugned Rent Order',
        'Certified Copy Awaited',
        'Memo of Appeal Drafting',
        'Filing',
        'Scrutiny / Objections, if any',
        'Admission / Preliminary Hearing',
        'Notice to Respondent',
        'Record Summoned / Received',
        'Stay / Suspension Application, if any',
        'Arguments',
        'Judgment / Final Order',
        'Further Remedy, if any',
        'Closed'
      ],
      aiContextHints: 'AI should understand impugned rent order, eviction/recovery/deposit issue, grounds of appeal, limitation, stay, record, and prayer.',
      subtypes: [
        'Appeal Against Eviction Order',
        'Appeal Against Dismissal of Eviction Petition',
        'Appeal Against Rent Recovery Order',
        'Appeal Against Interim Rent Order',
        'Appeal Against Rent Deposit Order',
        'Appeal Against Final Rent Order',
        'Other Rent Appeal'
      ]
    },
    {
      id: 'Rent Revision / Constitutional Challenge',
      label: 'Rent Revision / Constitutional Challenge',
      shortLabel: 'Revision / Writ',
      description: 'Revision, writ, or constitutional challenge against rent order, eviction order, appellate order, jurisdictional defect, or rent forum decision where maintainable.',
      legalBasis: [
        'Use applicable rent statute, revisional/writ/constitutional jurisdiction, and High Court jurisdiction where maintainable.'
      ],
      courtOptions: [
        { id: 'High Court', label: 'High Court', description: 'Common forum for constitutional/writ/revisional challenge against rent orders where maintainable.' },
        { id: 'Revisional Forum, where provided', label: 'Revisional Forum, where provided', description: 'Use where applicable rent law provides revision or special challenge forum.' },
        { id: 'Same Appellate / Rent Forum, where applicable', label: 'Same Appellate / Rent Forum, where applicable', description: 'Use only where local law provides review/correction within the forum.' },
        { id: 'Other / Custom Forum', label: 'Other / Custom Forum', description: 'Use if forum differs.' }
      ],
      courtHelper: 'Rent revision/writ maintainability depends on applicable rent law, appellate remedy, jurisdictional defect, and nature of impugned order.',
      sideOptions: ['Petitioner', 'Respondent', 'Landlord', 'Tenant'],
      documentTabs: [
        'All',
        'Revision / Writ Petition',
        'Applications',
        'Impugned Order / Record',
        'Rent Case Record',
        'Orders',
        'Miscellaneous'
      ],
      defaultStages: [
        'Impugned Order / Record Review',
        'Maintainability / Alternate Remedy Review',
        'Petition Drafting',
        'Filing',
        'Scrutiny / Objections, if any',
        'Preliminary Hearing / Admission',
        'Notice to Respondent',
        'Record Called, if required',
        'Stay / Suspension Application, if any',
        'Arguments',
        'Final Order',
        'Compliance / Further Remedy',
        'Closed'
      ],
      aiContextHints: 'AI should understand impugned rent order, jurisdictional error, misreading/non-reading of evidence, statutory remedy, High Court challenge, stay, and relief sought.',
      subtypes: [
        'Revision Against Rent Order',
        'Writ Against Rent Order',
        'Challenge to Eviction Order',
        'Challenge to Appellate Order',
        'Challenge to Interim Rent Order',
        'Challenge to Jurisdiction',
        'Other Rent Challenge'
      ]
    },
    {
      id: 'Rent Miscellaneous Application',
      label: 'Rent Miscellaneous Application',
      shortLabel: 'Application',
      description: 'Connected or standalone applications in rent/tenancy proceedings, including stay, rent deposit, restoration, setting aside ex parte proceedings, impleadment, amendment, or execution-related applications.',
      legalBasis: [],
      courtOptions: [
        { id: 'Same Rent Forum Where Case Is Pending', label: 'Same Rent Forum Where Case Is Pending', description: 'For applications connected with a pending rent case.' },
        { id: 'Rent Controller / Rent Tribunal', label: 'Rent Controller / Rent Tribunal', description: 'Use according to applicable province/territory.' },
        { id: 'Appellate Rent Forum / District Judge', label: 'Appellate Rent Forum / District Judge', description: 'For applications connected with a rent appeal.' },
        { id: 'High Court', label: 'High Court', description: 'For applications connected with rent revision/writ/constitutional challenge.' },
        { id: 'Other / Custom Forum', label: 'Other / Custom Forum', description: 'Use if forum differs.' }
      ],
      courtHelper: 'Application forum depends on the main rent case, pending forum, and nature of application.',
      sideOptions: ['Applicant', 'Respondent', 'Landlord', 'Tenant'],
      documentTabs: [
        'All',
        'Application',
        'Reply / Objections',
        'Supporting Documents',
        'Orders',
        'Miscellaneous'
      ],
      defaultStages: [
        'Application Preparation',
        'Filing',
        'Scrutiny / Objections, if any',
        'Notice to Respondent, if required',
        'Reply / Objections',
        'Arguments',
        'Order',
        'Compliance / Main Case Linkage',
        'Closed'
      ],
      aiContextHints: 'AI should understand main rent case, application subtype, landlord/tenant role, rent record, pending forum, urgency, supporting documents, and order sought.',
      subtypes: [
        'Stay Application',
        'Application for Deposit of Rent',
        'Application for Striking Off Defence',
        'Application for Restoration',
        'Application for Setting Aside Ex Parte Proceedings',
        'Application for Impleadment',
        'Application for Amendment',
        'Application for Local Commission / Inspection',
        'Application for Execution / Implementation',
        'Application for Early Hearing',
        'Other Rent Application'
      ]
    },
    {
      id: 'Other Rent / Tenancy Matter',
      label: 'Other Rent / Tenancy Matter',
      shortLabel: 'Other Rent',
      description: 'Use this for rent, tenancy, landlord-tenant, or premises-related matters that do not fit the available rent case types.',
      legalBasis: [],
      courtOptions: [
        { id: 'Competent Rent Forum / Rent Controller / Rent Tribunal', label: 'Competent Rent Forum / Rent Controller / Rent Tribunal', description: '' },
        { id: 'Civil Court', label: 'Civil Court', description: '' },
        { id: 'High Court', label: 'High Court, where applicable', description: '' },
        { id: 'Other / Custom Forum', label: 'Other / Custom Forum', description: '' }
      ],
      courtHelper: '',
      sideOptions: ['Party A', 'Party B', 'Landlord', 'Tenant', 'Applicant', 'Respondent'],
      documentTabs: [
        'All',
        'Documents',
        'Applications',
        'Rent Record',
        'Orders',
        'Miscellaneous'
      ],
      defaultStages: [
        'Case Preparation',
        'Filing / Institution',
        'Notice / Summons',
        'Reply / Response',
        'Rent Record / Evidence',
        'Arguments / Hearing',
        'Order / Judgment',
        'Compliance / Further Remedy',
        'Closed'
      ],
      aiContextHints: 'AI should rely on user-provided description, premises details, tenancy relationship, rent record, uploaded documents, forum, and selected relief.',
      subtypes: [
        'Custom Rent Matter',
        'Custom Tenancy Matter',
        'Other'
      ]
    }
  ]
};

export const getRentCaseTypes = () => RENT_CASE_TAXONOMY_CONFIG.types;

export const getRentSubtypes = (caseTypeId: string) => {
  const type = RENT_CASE_TAXONOMY_CONFIG.types.find(t => t.id === caseTypeId);
  return type ? type.subtypes : [];
};

export const normalizeRentCaseClassification = (existingCase: any) => {
  if (existingCase.caseCategory) {
    return existingCase; 
  }

  const legacyType = existingCase.matterType || existingCase.caseTypeName || existingCase.caseType || '';
  const lowerType = String(legacyType).toLowerCase();

  let caseCategory = undefined;
  let caseType = legacyType;
  let caseSubtype = undefined;

  // Render backward compatibility mappings
  if (lowerType === 'rent case') {
    caseCategory = 'Rent / Tenancy';
    caseType = 'Other Rent / Tenancy Matter';
  } else if (lowerType === 'rent petition' || lowerType === 'eviction petition' || lowerType === 'ejectment petition' || lowerType === 'landlord petition' || lowerType === 'tenant eviction') {
    caseCategory = 'Rent / Tenancy';
    caseType = 'Eviction / Ejectment Petition';
  } else if (lowerType === 'default in rent') {
    caseCategory = 'Rent / Tenancy';
    caseType = 'Eviction / Ejectment Petition';
    caseSubtype = 'Default in Payment of Rent';
  } else if (lowerType === 'personal need') {
    caseCategory = 'Rent / Tenancy';
    caseType = 'Eviction / Ejectment Petition';
    caseSubtype = 'Personal Bona Fide Need';
  } else if (lowerType === 'subletting') {
    caseCategory = 'Rent / Tenancy';
    caseType = 'Eviction / Ejectment Petition';
    caseSubtype = 'Subletting';
  } else if (lowerType === 'expiry of tenancy') {
    caseCategory = 'Rent / Tenancy';
    caseType = 'Eviction / Ejectment Petition';
    caseSubtype = 'Expiry of Tenancy';
  } else if (lowerType === 'rent recovery') {
    caseCategory = 'Rent / Tenancy';
    caseType = 'Rent Recovery';
  } else if (lowerType === 'arrears of rent') {
    caseCategory = 'Rent / Tenancy';
    caseType = 'Rent Recovery';
    caseSubtype = 'Arrears of Rent';
  } else if (lowerType === 'mesne profits' || lowerType === 'use and occupation charges') {
    caseCategory = 'Rent / Tenancy';
    caseType = 'Rent Recovery';
    caseSubtype = 'Mesne Profits / Use and Occupation Charges';
  } else if (lowerType === 'rent deposit' || lowerType === 'rent consignment') {
    caseCategory = 'Rent / Tenancy';
    caseType = 'Rent Deposit / Consignment';
  } else if (lowerType === 'deposit of rent') {
    caseCategory = 'Rent / Tenancy';
    caseType = 'Rent Deposit / Consignment';
    caseSubtype = 'Deposit of Monthly Rent';
  } else if (lowerType === 'tenant protection') {
    caseCategory = 'Rent / Tenancy';
    caseType = 'Tenant Protection / Injunction';
  } else if (lowerType === 'illegal dispossession') {
    caseCategory = 'Rent / Tenancy';
    caseType = 'Tenant Protection / Injunction';
    caseSubtype = 'Protection Against Illegal Dispossession';
  } else if (lowerType === 'forcible eviction') {
    caseCategory = 'Rent / Tenancy';
    caseType = 'Tenant Protection / Injunction';
    caseSubtype = 'Restraint Against Forcible Eviction';
  } else if (lowerType === 'utility disconnection') {
    caseCategory = 'Rent / Tenancy';
    caseType = 'Tenant Protection / Injunction';
    caseSubtype = 'Restoration of Utilities';
  } else if (lowerType === 'rent appeal') {
    caseCategory = 'Rent / Tenancy';
    caseType = 'Rent Appeal';
  } else if (lowerType === 'appeal against eviction order') {
    caseCategory = 'Rent / Tenancy';
    caseType = 'Rent Appeal';
    caseSubtype = 'Appeal Against Eviction Order';
  } else if (lowerType === 'appeal against rent order') {
    caseCategory = 'Rent / Tenancy';
    caseType = 'Rent Appeal';
    caseSubtype = 'Appeal Against Final Rent Order';
  } else if (lowerType === 'rent revision') {
    caseCategory = 'Rent / Tenancy';
    caseType = 'Rent Revision / Constitutional Challenge';
  } else if (lowerType === 'writ against rent order') {
    caseCategory = 'Rent / Tenancy';
    caseType = 'Rent Revision / Constitutional Challenge';
    caseSubtype = 'Writ Against Rent Order';
  } else if (lowerType === 'challenge to eviction order') {
    caseCategory = 'Rent / Tenancy';
    caseType = 'Rent Revision / Constitutional Challenge';
    caseSubtype = 'Challenge to Eviction Order';
  } else if (lowerType === 'rent miscellaneous application') {
    caseCategory = 'Rent / Tenancy';
    caseType = 'Rent Miscellaneous Application';
  } else if (lowerType === 'stay application in rent case') {
    caseCategory = 'Rent / Tenancy';
    caseType = 'Rent Miscellaneous Application';
    caseSubtype = 'Stay Application';
  } else if (lowerType === 'restoration in rent case') {
    caseCategory = 'Rent / Tenancy';
    caseType = 'Rent Miscellaneous Application';
    caseSubtype = 'Application for Restoration';
  } else if (lowerType === 'setting aside ex parte in rent case') {
    caseCategory = 'Rent / Tenancy';
    caseType = 'Rent Miscellaneous Application';
    caseSubtype = 'Application for Setting Aside Ex Parte Proceedings';
  } else if (RENT_CASE_TAXONOMY_CONFIG.types.some(t => t.id.toLowerCase() === lowerType)) {
    caseCategory = 'Rent / Tenancy';
    caseType = RENT_CASE_TAXONOMY_CONFIG.types.find(t => t.id.toLowerCase() === lowerType)?.id || caseType;
  }

  // Handle generic 'Rent' mapping to Eviction / Ejectment Petition if there's no better fit
  if (!caseCategory && lowerType === 'rent') {
      caseCategory = 'Rent / Tenancy';
      caseType = 'Other Rent / Tenancy Matter';
  }

  return {
    ...existingCase,
    ...(caseCategory && { caseCategory }),
    caseType,
    ...(caseSubtype && { caseSubtype }),
    legacyCaseType: legacyType
  };
};
